<template>
  <div class="email-setting">
    <h3 class="text-xl font-bold text-neutral-900">{{ $t('Email Settings') }}</h3>
    <p class="text-neutral-600 mt-1 mb-6">
      {{ $t('Use the settings below to manage and configure your email settings.') }}
    </p>
    <h4 class="font-semibold text-neutral-900">
      {{ $t('Email sender name') }}
    </h4>
    <p class="text-neutral-600 mt-1 mb-2">
      {{ $t('Your users will see this as the sender in all emails sent by FeedBear.') }}
    </p>
    <form action @submit.prevent="saveEmailSettings()">
      <input
        id="project-name"
        :placeholder="$t('Project Name')"
        v-model="emailSender"
        autocomplete="off"
        type="text"
        name="post[project_name]"
        class="input mb-5 lg:w-1/2 sm:w-full"
        required
      />
      <h4 class="font-semibold text-neutral-900 mt-6">
        {{ $t('Mailing address') }}
      </h4>
      <p class="text-neutral-600 mt-1 mb-2">
        {{ $t('Business address to show at the bottom of all emails. This should be a PO Box or physical mailing address.') }}
      </p>
      <input
        id="mailing-address"
        :placeholder="$t('5603 Manitou Road, Minnetrista, MN 55331')"
        v-model="mailingAddress"
        autocomplete="off"
        type="text"
        name="post[mailing_address]"
        class="input mb-5 lg:w-1/2 sm:w-full"
        required
      />
      <h4 class="font-semibold text-neutral-900 mt-6">
        {{ $t('Reply-to address') }}
      </h4>
      <p class="text-neutral-600 mt-1 mb-2">
        {{ $t('Enter a public email address where you want to receive emails from users. e.g. support@domain.com') }}
      </p>
      <input
        id="reply-to-address"
        :placeholder="$t('no-reply@feedbear.com')"
        v-model="replyToAddress"
        autocomplete="off"
        type="text"
        name="post[mailing_address]"
        class="input mb-5 lg:w-1/2 sm:w-full"
        required
      />

      <div class="" v-if="currentUserOwner">
        <h4 class="font-semibold text-neutral-900 mt-8">
          {{ $t('Email notifications') }}
        </h4>

        <p class="text-neutral-600 mt-1">
          {{ $t('Use the options below to choose which actions email your users and your team.') }}
        </p>
        <p class="text-neutral-600 mt-1 mb-2">
          {{ $t('If an action is enabled, users will be able to enable or disable notifications in their account settings if they want to.') }}
        </p>
        <div class="mt-4 mb-8">
          <div class="toggle-wrapper toggle-wrapper--row mt-5">
            <div class="button--toggle">
              <input type="checkbox" id="new_comment_notification" v-model="newCommentNotification" class="checkbox" true-value="1" false-value="0" :disabled="!loaded" />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
            <div>
              <label for="new_comment_notification" class="cursor-pointer font-medium text-neutral-800 block">{{ $t('New comment on an idea') }}</label>
              <span class="block text-neutral-700">{{ $t('Notifies authors about new comments on ideas they posted') }}</span>
            </div>
          </div>

          <div class="toggle-wrapper toggle-wrapper--row mt-5">
            <div class="button--toggle">
              <input type="checkbox" id="idea_status_update_notification" v-model="ideaStatusUpdateNotification" class="checkbox" true-value="1" false-value="0" :disabled="!loaded" />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
            <div>
              <label for="idea_status_update_notification" class="cursor-pointer font-medium text-neutral-800 block">{{ $t('Status update of ideas') }}</label>
              <span class="block text-neutral-700">{{ $t('Notifies authors about status changes (e.g. from Planned to Done) on ideas') }}</span>
              <span class="block text-neutral-500 text-sm">{{ $t('Not sent when the new status is Not now.') }}</span>
            </div>
          </div>

          <div class="toggle-wrapper toggle-wrapper--row mt-5">
            <div class="button--toggle">
              <input type="checkbox" v-model="upvotedIdeaStatusUpdateNotification" id="upvoted_idea_status_update_notification" class="checkbox" true-value="1" false-value="0" :disabled="!loaded" />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
            <div>
              <label for="upvoted_idea_status_update_notification" class="cursor-pointer font-medium text-neutral-800 block">{{ $t('Status update of upvoted ideas') }}</label>
              <span class="block text-neutral-700">{{ $t('Notifies users who upvoted an idea when the status changes') }}</span>
              <span class="block text-neutral-500 text-sm">{{ $t('Not sent when the new status is Not now.') }}</span>
            </div>
          </div>

          <div class="toggle-wrapper toggle-wrapper--row mt-5">
            <div class="button--toggle">
              <input type="checkbox" v-model="upvotedIdeaNewCommentNotification" id="upvoted_idea_new_comment_notification" class="checkbox" true-value="1" false-value="0" :disabled="!loaded" />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
            <div>
              <label for="upvoted_idea_new_comment_notification" class="cursor-pointer font-medium text-neutral-800 block">{{ $t('Team comments on upvoted ideas') }}</label>
              <span class="block text-neutral-700">{{ $t('Notifies users who upvoted an idea when your team comments on it') }}</span>
            </div>
          </div>

          <div class="toggle-wrapper toggle-wrapper--row mt-5">
            <div class="button--toggle">
              <input type="checkbox" v-model="newMentionNotification" id="new_mention_notification" class="checkbox" true-value="1" false-value="0" :disabled="!loaded" />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
            <div>
              <label for="new_mention_notification" class="cursor-pointer font-medium text-neutral-800 block">{{ $t('New mention in a comment') }}</label>
              <span class="block text-neutral-700">{{ $t('Notifies users when they are mentioned in a comment') }}</span>
            </div>
          </div>

          <div class="toggle-wrapper toggle-wrapper--row mt-5">
            <div class="button--toggle">
              <input type="checkbox" id="new_idea_notification" v-model="newIdeaNotification" class="checkbox" true-value="1" false-value="0" :disabled="!loaded" />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
            <div>
              <label for="new_idea_notification" class="cursor-pointer font-medium text-neutral-800 block">{{ $t('New ideas') }}</label>
              <span class="block text-neutral-700">{{ $t('Notifies admin users when a new idea is posted on a board') }}</span>
            </div>
          </div>

          <div class="toggle-wrapper toggle-wrapper--row mt-5">
            <div class="button--toggle">
              <input type="checkbox" id="admin_comment_notification" v-model="adminCommentNotification" class="checkbox" true-value="1" false-value="0" :disabled="!loaded" />
              <div class="knobs"></div>
              <div class="layer"></div>
            </div>
            <div>
              <label for="admin_comment_notification" class="cursor-pointer font-medium text-neutral-800 block">{{ $t('New comments (admin)') }}</label>
              <span class="block text-neutral-700">{{ $t("Notifies admin users when there's a new comment on any idea") }}</span>
            </div>
          </div>
        </div>
      </div>

      <div class="" v-else>
        <h4 class="font-semibold text-neutral-900 mt-8">
          {{ $t('Email notifications') }}
        </h4>

        <p class="text-neutral-600 mt-1 mb-2">
          {{ $t('Owners and admins manage these options. To manage your own email notifications, visit your Account settings.') }}
        </p>
      </div>
      <p v-if="loadFailed" class="text-red-600 mt-6">{{ $t("We couldn't load your current settings. Refresh the page and try again.") }}</p>
      <input type="submit" class="btn-main mt-8" :value="$t('Save')" :disabled="!loaded" />
    </form>
  </div>
</template>

<script>
export default {
  name: "email-setting",
  props: {
    saveEmailSettingsUrl: String,
    getEmailSettingsUrl: String,
    currentUserOwner: Boolean,
  },
  data() {
    return {
      emailSender: "",
      mailingAddress: "",
      replyToAddress: "",
      newCommentNotification: false,
      newIdeaNotification: false,
      newMentionNotification: false,
      upvotedIdeaStatusUpdateNotification: false,
      ideaStatusUpdateNotification: false,
      adminCommentNotification: false,
      upvotedIdeaNewCommentNotification: false,
      // Save posts every toggle, so nothing may be saved before the real
      // values are in: a failed load would otherwise write them all as off.
      loaded: false,
      loadFailed: false,
    };
  },
  mounted() {
    Rails.ajax({
      url: this.getEmailSettingsUrl,
      type: "GET",
      beforeSend: function () {
        return true;
      },
      success: (data) => {
        if (data.email_settings.email_sender_name)
          this.emailSender = data.email_settings.email_sender_name;
        else this.emailSender = "";
        if (data.email_settings.mailing_address)
          this.mailingAddress = data.email_settings.mailing_address;
        else this.mailingAddress = "";
        if (data.email_settings.reply_to_address)
          this.replyToAddress = data.email_settings.reply_to_address;
        else this.replyToAddress = "";
        this.newCommentNotification = data.email_settings
          .new_comment_notification
          ? 1
          : 0;
        this.newIdeaNotification = data.email_settings.new_idea_notification
          ? 1
          : 0;
        this.newMentionNotification = data.email_settings
          .new_mention_notification
          ? 1
          : 0;
        this.upvotedIdeaStatusUpdateNotification = data.email_settings
          .upvoted_idea_status_update_notification
          ? 1
          : 0;
        this.ideaStatusUpdateNotification = data.email_settings
          .idea_status_update_notification
          ? 1
          : 0;
        this.adminCommentNotification = data.email_settings
          .admin_comment_notification
          ? 1
          : 0;
        this.upvotedIdeaNewCommentNotification = data.email_settings
          .upvoted_idea_new_comment_notification
          ? 1
          : 0;
        this.loaded = true;
      },
      error: (data, status) => {
        this.loadFailed = true;
        console.log(data);
      },
    });
  },
  methods: {
    saveEmailSettings() {
      if (!this.loaded) return;
      let data = new FormData();
      data.append("email_sender_name", this.emailSender);
      data.append("mailing_address", this.mailingAddress);
      data.append("reply_to_address", this.replyToAddress);
      data.append(
        "project[new_comment_notification]",
        this.newCommentNotification
      );
      data.append("project[new_idea_notification]", this.newIdeaNotification);
      data.append(
        "project[new_mention_notification]",
        this.newMentionNotification
      );
      data.append(
        "project[upvoted_idea_status_update_notification]",
        this.upvotedIdeaStatusUpdateNotification
      );
      data.append(
        "project[idea_status_update_notification]",
        this.ideaStatusUpdateNotification
      );
      data.append(
        "project[admin_comment_notification]",
        this.adminCommentNotification
      );
      data.append(
        "project[upvoted_idea_new_comment_notification]",
        this.upvotedIdeaNewCommentNotification
      );
      Rails.ajax({
        url: this.saveEmailSettingsUrl,
        type: "POST",
        data: data,
        dataType: "json",
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          window.location.reload();
        },
        error: (data, status) => {
          console.log(data);
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.email-setting {
  .desc-text {
    color: #666666;
  }
  .label {
    color: #525252;
  }
  .submit-btn {
    background-color: #333333;
  }
  .submit-btn:hover {
    background: white;
  }
}
</style>

<style scoped>
.toggle-wrapper {
  display: flex;
  align-items: center;
}

.toggle-wrapper--row {
  align-items: center;
  gap: 0.25rem;
}

.button--toggle {
  position: relative;
  width: 74px;
  height: 36px;
  overflow: hidden;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.button--toggle,
.button--toggle .layer {
  border-radius: 100px;
}

.checkbox {
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 3;
}

.knobs,
.layer {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}

.knobs {
  z-index: 2;
}

.layer {
  width: 100%;
  background-color: #f5f5f5;
  transition: 0.3s ease all;
  z-index: 1;
  border: 1px solid gainsboro;
}

.button--toggle .knobs:before {
  content: "";
  position: absolute;
  top: 4px;
  left: 4px;
  width: 28px;
  height: 28px;
  background-color: #d2d2d2;
  border-radius: 50%;
  transition: 0.3s ease all, left 0.3s cubic-bezier(0.18, 0.89, 0.35, 1.15);
}

.button--toggle .checkbox:active + .knobs:before {
  width: 46px;
  border-radius: 100px;
}

.button--toggle .checkbox:checked:active + .knobs:before {
  margin-left: -26px;
}

.button--toggle .checkbox:checked + .knobs:before {
  content: "";
  left: 42px;
  background-color: #3bd087;
}

.button--toggle .checkbox:checked ~ .layer {
  background-color: #d1f0e1;
  border-color: #a3e6c6;
}
</style>
